import { types, flow, Instance } from "mobx-state-tree";
import axios from "axios";
import {
  CREATE_NEW_PAYMENTS_API,
  OUTSTANDING_INVOICE_API,
  PAYMENTS_API,
  PAYMENT_METHODS_API,
} from "@constants/api";
import { AlertResultApi } from "@utils//alert";
import { OutstandingDataType, SelectedItemType } from "./Outstanding";
import { fetching, FetchingEnum } from "./Fetching";

export const initialStatePayment = {
  fetchingCreatePayment: FetchingEnum.DONE,
  fetchingOutstandingPrice: FetchingEnum.DONE,
  fetchingPaymnetMethods: FetchingEnum.DONE,
  fetchingRequestPaymentMethod: FetchingEnum.DONE,
  fetchingAttachCreditCard: FetchingEnum.DONE,
  fetchingRemoveCreditCard: FetchingEnum.DONE,
  fetchingSetDefaultCreditCard: FetchingEnum.DONE,
  fetchingCheckBankCharge: FetchingEnum.DONE,
};

const InvoicePayment = types.model({
  id: types.number,
  appliedCreditNotes: types.maybeNull(types.string),
  paymentId: types.number,
  invoiceAccountingId: types.number,
  amount: types.number,
  description: types.string,
});

const CreatePayment = types.model({
  id: types.number,
  paymentPlatform: types.maybeNull(types.string),
  paymentRef: types.maybeNull(types.string),
  description: types.string,
  url: types.maybeNull(types.string),
  callbackLog: types.maybeNull(types.string),
  amount: types.number,
  expiredAt: types.maybeNull(types.string),
  status: types.string,
  createdAt: types.string,
  updatedAt: types.string,
  invoices: types.optional(types.array(InvoicePayment), []),
});

const PaymentMethod = types.model({
  paymentMethodId: types.string,
  lastDigit: types.string,
  cardType: types.string,
  isDefault: types.boolean,
});

const RequestPaymentMethod = types.model({
  clientSecret: types.string,
});

const BackCharge = types.model({
  bankCharges: types.number,
});

export const Payment = types
  .model("Payment", {
    createPayment: types.maybeNull(CreatePayment),
    fetchingCreatePayment: fetching,
    statusCreatePayment: types.maybeNull(types.number),

    // Price Oustanding
    outstandingPrice: types.maybeNull(types.number),
    fetchingOutstandingPrice: fetching,
    statusOutstandingPrice: types.maybeNull(types.number),

    // list Credit Card
    paymentMethods: types.maybeNull(types.array(PaymentMethod)),
    fetchingPaymnetMethods: fetching,
    statusPaymentMethods: types.maybeNull(types.number),

    // Request Credit Card
    requestPaymentMethod: types.maybeNull(RequestPaymentMethod),
    fetchingRequestPaymentMethod: fetching,
    statusRequestPaymentMethod: types.maybeNull(types.number),

    // Attach Credit Card
    attachCreditCard: types.maybeNull(PaymentMethod),
    fetchingAttachCreditCard: fetching,
    statusAttachCreditCard: types.maybeNull(types.number),

    // Remove Credit Card
    fetchingRemoveCreditCard: fetching,
    statusRemoveCreditCard: types.maybeNull(types.number),

    // Set Default Credit Card
    fetchingSetDefaultCreditCard: fetching,
    statusSetDefaultCreditCard: types.maybeNull(types.number),

    // Check Bank Charge
    checkBankCharge: types.maybeNull(BackCharge),
    fetchingCheckBankCharge: fetching,
    statusCheckBankCharge: types.maybeNull(types.number),

    // Tab Payment State
    activeInvoiceTabSection: types.optional(types.string, "0"),
  })
  .actions((self) => ({
    initialPayment: () => {
      self.requestPaymentMethod = null;
      self.fetchingRequestPaymentMethod = FetchingEnum.DONE;
      self.statusRequestPaymentMethod = null;
      self.attachCreditCard = null;
      self.fetchingAttachCreditCard = FetchingEnum.DONE;
      self.statusAttachCreditCard = null;
    },
    resetNewPayment: () => {
      self.fetchingCreatePayment = "done";
      self.statusCreatePayment = null;
      self.createPayment = null;
    },
    resetRemoveAndSetDefaultCreditCard: () => {
      self.fetchingRemoveCreditCard = "done";
      self.statusRemoveCreditCard = null;
      self.fetchingSetDefaultCreditCard = "done";
      self.statusSetDefaultCreditCard = null;
    },
    createNewPayment: flow(function* createNewPayment(
      token: string,
      outstandingItems: SelectedItemType[],
      type: string,
      paymentMethod: string,
      callback: (
        data: CreatePaymentType | StripePaymentConfrimType
      ) => Promise<void>
    ) {
      self.fetchingCreatePayment = "pending";
      self.statusCreatePayment = null;
      self.createPayment = null;
      try {
        let invoice: number[] = [];
        let creditNote: number[] = [];
        outstandingItems.forEach((item) => {
          if (item.type === "invoice" && item.accountingDocumentId) {
            invoice.push(item.accountingDocumentId);
          } else if (item.type === "credit_note" && item.accountingDocumentId) {
            creditNote.push(item.accountingDocumentId);
          }
        });

        if (invoice.length === 0 && creditNote.length === 0) {
          self.fetchingCreatePayment = "done";
          self.statusCreatePayment = 400;
          AlertResultApi(400, "No Credit Note or Invoice selected");
          return;
        }

        const result = yield axios({
          method: "post",
          url: CREATE_NEW_PAYMENTS_API,
          headers: { Authorization: `Bearer ${token}` },
          data: {
            invoiceRefs: invoice,
            creditNoteRefs: creditNote,
            platform: type,
            paymentMethod: paymentMethod,
          },
        });

        self.statusCreatePayment = result.status;
        self.createPayment = result.data;
        self.fetchingCreatePayment = "done";
        callback(result.data);
      } catch (error: any) {
        // console.error('Failed to fetch projects', error);
        if (error && error.response) {
          // console.error('Failed to fetch projects', error.response);
          self.statusCreatePayment = error.response.status;
          AlertResultApi(error.response.status, error.response.data.message);
        } else {
          self.statusCreatePayment = 500;
          AlertResultApi(500);
        }
        self.fetchingCreatePayment = "error";
      }
    }),
    getListPaymentMethods: flow(function* getListPaymentMethods(
      provider: string,
      token: string,
      callback: (data: PaymentMethodType[]) => Promise<void>
    ) {
      self.fetchingPaymnetMethods = "pending";
      self.paymentMethods = null;
      self.statusPaymentMethods = null;
      try {
        const result = yield axios.get(`${PAYMENT_METHODS_API}/${provider}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        self.paymentMethods = result.data;
        self.fetchingPaymnetMethods = "done";
        self.statusPaymentMethods = result.status;
        callback(result.data);
      } catch (error: any) {
        self.fetchingPaymnetMethods = "error";
        self.statusPaymentMethods = error?.response?.status || 500;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    getRequestPaymentMehods: flow(function* requestPaymentMehods(
      provider: string,
      token: string
    ) {
      self.fetchingRequestPaymentMethod = "pending";
      self.requestPaymentMethod = null;
      self.statusRequestPaymentMethod = null;
      try {
        const result = yield axios.get(
          `${PAYMENT_METHODS_API}/request/${provider}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        self.requestPaymentMethod = result.data;
        self.fetchingRequestPaymentMethod = "done";
        self.statusRequestPaymentMethod = result.status;
      } catch (error: any) {
        self.fetchingRequestPaymentMethod = "error";
        self.statusRequestPaymentMethod = error?.response?.status || 500;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    postAttachPaymentMethod: flow(function* postAttachPaymentMethod(
      provider: string,
      token: string,
      paymentMethodId: string,
      isDefault: boolean
    ) {
      self.attachCreditCard = null;
      self.fetchingAttachCreditCard = "pending";
      self.statusAttachCreditCard = null;
      try {
        const result = yield axios.request({
          method: "post",
          url: `${PAYMENT_METHODS_API}/attach`,
          headers: { Authorization: `Bearer ${token}` },
          data: {
            provider,
            paymentMethodId,
            isDefault,
          },
        });

        self.attachCreditCard = result.data;
        self.fetchingAttachCreditCard = "done";
        self.statusAttachCreditCard = result.status;
      } catch (error: any) {
        self.fetchingAttachCreditCard = "error";
        self.statusAttachCreditCard = error?.response?.status || 500;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    removeCreditCard: flow(function* removeCreditCard(
      provider: string,
      token: string,
      paymentMethodId: string
    ) {
      self.fetchingRemoveCreditCard = "pending";
      self.statusRemoveCreditCard = null;
      try {
        const result = yield axios.delete(
          `${PAYMENT_METHODS_API}/${provider}/${paymentMethodId}/remove`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        self.statusRemoveCreditCard = result.status;
        self.fetchingRemoveCreditCard = "done";
      } catch (error: any) {
        self.fetchingRemoveCreditCard = "error";
        self.statusRemoveCreditCard = error?.response?.status || 500;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    setDefaultCreditCard: flow(function* setDefaultCreditCard(
      provider: string,
      token: string,
      paymentMethodId: string
    ) {
      self.fetchingSetDefaultCreditCard = "pending";
      self.statusSetDefaultCreditCard = null;
      try {
        const result = yield axios.put(
          `${PAYMENT_METHODS_API}/${provider}/${paymentMethodId}/setDefault`,
          undefined,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        self.statusSetDefaultCreditCard = result.status;
        self.fetchingSetDefaultCreditCard = "done";
      } catch (error: any) {
        self.fetchingSetDefaultCreditCard = "error";
        self.statusSetDefaultCreditCard = error?.response?.status || 500;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    getCheckBackCharge: flow(function* getCheckBackCharge(
      provider: string,
      token: string,
      amount: number
    ) {
      self.fetchingCheckBankCharge = "pending";
      self.checkBankCharge = null;
      self.statusCheckBankCharge = null;
      try {
        const result = yield axios.get(
          `${PAYMENTS_API}/${provider}/checkBankCharges?amount=${amount}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        self.checkBankCharge = result.data;
        self.fetchingCheckBankCharge = "done";
        self.statusCheckBankCharge = result.status;
      } catch (error: any) {
        self.fetchingCheckBankCharge = "error";
        self.statusCheckBankCharge = error?.response?.status || 500;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
    setActiveInvoiceTabSection: (activeTab: string) => {
      self.activeInvoiceTabSection = activeTab;
    },
  }));

export type PaymentMethodType = Instance<typeof PaymentMethod>;
